/**
 * A care manager's planned visit (0240): the client's visits from a date,
 * add one, remove one. A visit is a plan: it shows until its date and
 * nowhere after.
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';
import { cmHomeTenantKey } from './cmHomeApi';
import { clientRecordKey } from './clientRecordApi';
import { apiErrorFrom } from './api';
import { writeFetch } from './writeFetch';

export interface CareVisit {
  id: string;
  client_id: string;
  staff_roster_id: string;
  care_manager_name: string;
  /** 'YYYY-MM-DD'. */
  scheduled_for: string;
  /** 'HH:MM' or null. */
  scheduled_time: string | null;
  note: string | null;
  created_at: string;
}

export interface CareVisitInput {
  /** The office names the care manager; a care manager's own is the default. */
  staff_roster_id?: string;
  scheduled_for: string;
  scheduled_time: string | null;
  note: string | null;
}

const base = (slug: string, clientId: string): string =>
  `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/care-visits`;

export function careVisitsKey(slug: string, clientId: string): readonly unknown[] {
  return ['careVisits', slug, clientId];
}

export const careVisitsApi = {
  async list(slug: string, clientId: string, from: string): Promise<CareVisit[]> {
    const url = `${base(slug, clientId)}?from=${encodeURIComponent(from)}`;
    const r = await fetch(url, { headers: { accept: 'application/json' } });
    if (!r.ok) throw await apiErrorFrom(r, `GET ${url}`);
    return ((await r.json()) as { visits: CareVisit[] }).visits;
  },
  async create(slug: string, clientId: string, input: CareVisitInput): Promise<CareVisit> {
    const url = base(slug, clientId);
    const r = await writeFetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    if (!r.ok) throw await apiErrorFrom(r, `POST ${url}`);
    return ((await r.json()) as { visit: CareVisit }).visit;
  },
  async remove(slug: string, clientId: string, visitId: string): Promise<void> {
    const url = `${base(slug, clientId)}/${encodeURIComponent(visitId)}`;
    const r = await writeFetch(url, { method: 'DELETE' });
    if (!r.ok) throw await apiErrorFrom(r, `DELETE ${url}`);
  },
};

/** The client's visits from `today` on; the caller hides any before it. */
export function useCareVisits(slug: string, clientId: string, today: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: [...careVisitsKey(slug, clientId), today],
    queryFn: () => careVisitsApi.list(slug, clientId, today),
    enabled: (opts.enabled ?? true) && slug !== '' && clientId !== '',
  });
}

/** After a visit is saved or removed, every reader refetches: the
 *  client's visit list (the record's Appointments card), the record read
 *  (its header's next appointment) and the home read (My Clients' Today
 *  band and the row's next visit). */
export async function refreshAfterCareVisitChange(queryClient: QueryClient, slug: string, clientId: string): Promise<void> {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: careVisitsKey(slug, clientId) }),
    queryClient.invalidateQueries({ queryKey: cmHomeTenantKey(slug) }),
    queryClient.invalidateQueries({ queryKey: clientRecordKey(slug, clientId) }),
  ]);
}

/** The soonest visit on `today` or later: the earlier date first, then the
 *  earlier time, an untimed visit after a timed one on the same day. */
export function soonestCareVisit(visits: readonly CareVisit[], today: string): CareVisit | null {
  const key = (v: CareVisit): string => `${v.scheduled_for}T${v.scheduled_time ?? '~'}`;
  const ahead = visits.filter((v) => visitStillAhead(v, today)).sort((a, b) => (key(a) < key(b) ? -1 : key(a) > key(b) ? 1 : 0));
  return ahead[0] ?? null;
}

/** Shown on its date and before it; never after. */
export function visitStillAhead(v: Pick<CareVisit, 'scheduled_for'>, today: string): boolean {
  return v.scheduled_for >= today;
}
